import murmur from 'murmurhash-js'

import { useMarket } from 'src/hooks/useMarket'
import { useDevice } from 'src/hooks/useDevice'
import { useSession } from 'src/hooks/useSession'
import { useBusiness } from 'src/hooks/useBusiness'
import { PAGES, PAGES_KEYS, SECTION_KEYS } from './constants'
import { ORIGIN_DIRECT } from 'client/contexts/Tracking/constants'
import { useS4LContext } from '@s4l/react-context'

const SEED = 87
const EVENT_NAME = 'click'
const EVENT_TYPE = 'click'
const LEVEL = 'info'
const EVENT_SUB_TYPE = null
const PLATFORM_WEB = 'web'
const PLATFORM_APP = 'app'
const SCOPE = 'tracking'

export function trackingKafkaRequest(data: Record<string, any>) {
  fetch(`${process.env.NEXT_PUBLIC_TRACKING_ENDPOINT}`, {
    method: 'POST',
    body: JSON.stringify({
      records: [{ key: null, value: data }],
    }),
    headers: {
      'Content-Type': 'application/vnd.kafka.json.v2+json',
      Accept: 'application/vnd.kafka.v2+json',
    },
  })
}

type IsSnakeCase<T extends string> =
  T extends Lowercase<T>
    ? T extends `${string}-${string}`
      ? never
      : T extends `${string}_${infer Rest}`
        ? Rest extends IsSnakeCase<Rest>
          ? T
          : never
        : T
    : never

interface UseKafkaEventParams<T extends string> {
  eventName?: IsSnakeCase<T>
  eventType?: string
  eventSubType?: string | null
  page: string
}

export const useKafkaEvent = <T extends string>({
  eventName,
  eventType = EVENT_TYPE,
  eventSubType = EVENT_SUB_TYPE,
  page,
}: UseKafkaEventParams<T>) => {
  const { language, market } = useMarket()
  const { device, ip, isBot } = useDevice()
  const { customer, id } = useSession()
  const { affiliate } = useBusiness()
  const { mobileAppVersion } = useS4LContext()

  const pageKey = typeof page === 'string' ? page.toUpperCase() : page
  const pageName = PAGES_KEYS.includes(pageKey) ? PAGES[pageKey as keyof typeof PAGES] : page

  const sendEvent = (
    { section }: { section: (typeof SECTION_KEYS)[number] },
    extraPayload: Record<string, any> = {} // eslint-disable-line
  ) => {
    const timestamp = Date.now()
    const kafkaData = {
      level: LEVEL,
      event_sub_type: eventSubType,
      platform: mobileAppVersion ? PLATFORM_APP : PLATFORM_WEB,
      event_type: eventType,
      scope: SCOPE,
      event_name: eventName ?? EVENT_NAME,
      hsl: murmur(`${eventName ?? EVENT_NAME}${timestamp}${eventType}`, SEED),
      ip,
      is_bot: isBot,
      created_at: timestamp,
      screen: `${window.screen.width}x${window.screen.height}`,
      session_id: id,
      language,
      market: market.toUpperCase(),
      user_id: customer?.userId ?? null,
      device,
      user_agent: navigator.userAgent,
      payload: {
        affiliate,
        origin: document.referrer ? new URL(document.referrer).origin : ORIGIN_DIRECT,
        page: pageName,
        section,
        ...extraPayload,
      },
    }

    trackingKafkaRequest(kafkaData)
  }

  return { sendEvent }
}
